import PropTypes from 'prop-types';
import React from 'react';
import { Link, browserHistory } from 'react-router';
import styles from './RegionEAE.css';
import withStyles from '../../decorators/withStyles';
import {simplePlaceName} from "../../utils/utils";
import {sameLocationNewHash} from "../../utils/utils";
import * as d3 from 'd3';
import ComponentIcons from "../ComponentIcons";
import AnchoredComponent from "../AnchoredComponent";
import BarChart from "../BarChart";
import PresetButtons from "../PresetButtons";
import DownloadForm from "../DownloadForm";
import ToggleText from "../ToggleText";
import Modal from "../Modal";
import ReactDOM from 'react-dom';
import { SetModalVisible, SetEAEVariable, SetEAEDerivative,
         SetEAEIsPct, SetEAESensitivity, SetEAEScenario, SetModalTitle } from "../../actions";
import { capitalize, commas, downloadImage, downloadFile, gaEvent, xlt } from "../../utils/utils";
import { DL_SERVICE_URL } from "../../config";
import { pngIcon } from "../../icons";


class RegionEAE extends AnchoredComponent {

  static propTypes = {
    placeType: PropTypes.string.isRequired,
    placeID: PropTypes.string.isRequired,
    placeName: PropTypes.string.isRequired,
    placeContainer: PropTypes.object.isRequired,
  };

  static defaultProps = {
    presets: ["RCP 2.6", "RCP 4.5", "RCP 8.5"]
  };

  constructor(props) {
    super(props);

    this.margin = {top: 20, right: 20, bottom: 60, left: 80};
    this.width = 680 - this.margin.left - this.margin.right;
    this.height = 340 - this.margin.top - this.margin.bottom;

    this.state = {
      downloadType: "eae-img",
      settingsOpen: false,
      downloadOpen: false,
    };

    this.varToggleCount = 0;
  }

  setTxContent = el => {
    d3.select(el).attr("tx-content", "block");
    // d3.select(el).attr("tx-dynamic", "disabled");
  };

  openInfo = e => {
    gaEvent('modal', 'open', 'info-eae');
    browserHistory.push({pathname: "/about", hash: "#about-eae",
      state: {modal: true, modalTitle: "About this site", returnTo: location.pathname}}
    );
  };

  closeSettings = () => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({settingsOpen: false});
  };

  openSettings = (hashTarget) => {
    gaEvent('modal', 'open', 'settings-eae');
    this.props.dispatch(SetModalVisible(true));
    this.setState({settingsOpen: true}, () => {
      if (hashTarget) {
        const el = ReactDOM.findDOMNode(this).querySelector(hashTarget);
        if (el) {
          setTimeout(() => {
            el.scrollIntoView();
          }, 100);
        }
      }
    });
  };

  setDlType(dlType) {
    if (dlType === "eae-img") {
      this.props.dispatch(SetModalTitle("Get image"));
    } else if (dlType === "eae-pptx") {
      this.props.dispatch(SetModalTitle("Get slide"));
    }
    this.setState({dlType: dlType});
  }

  handleDownloadClick = dlType => {
    gaEvent('modal', 'open', `download-${dlType}`);
    this.setDlType(dlType);
    this.openDownload();
  };

  closeAll = () => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({downloadOpen: false, settingsOpen: false});
  };

  openDownload = e => {
    this.closeAll();
    this.props.dispatch(SetModalVisible(true));
    this.setState({downloadOpen: true})
  };

  closeDownload = e => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({downloadOpen: false});
  };

  ssTarget() {
    const { placeType, placeID, eaeIsPct, eaeDerivative, eaeScenario, eaeVariable, eaePercentile } = this.props;
    const lang = Transifex.live.getSelectedLanguageCode();
    const url = `http://riskfinder.climatecentral.org/embed/eae-img/${placeType}/${placeID}?eaeScenario=${encodeURIComponent(eaeScenario)}&eaeVariable=${eaeVariable}&eaePercentile=${eaePercentile}&eaeDerivative=${eaeDerivative}&eaeIsPct=${eaeIsPct}&lang=${lang}`;
    return url;
  }

  dlCallback = (onDlComplete) => {
    const { placeID} = this.props;
    var url;
    if (this.state.dlType === "eae-img") {
      const filename = `surging-seas-forecast-${placeID}.png`;
      url = DL_SERVICE_URL + "/screenshot" + `?filename=${filename}&width=1024&height=768&url=` + encodeURIComponent(this.ssTarget());
      downloadImage(url, "riskfinder.png", onDlComplete);
    } else if (this.state.dlType === "eae-pptx") {
      const filename = `surging-seas-forecast-${placeID}.pptx`;
      url = DL_SERVICE_URL + "/screenshot" + `?filename=${filename}&format=pptx&width=1024&height=768&url=` + encodeURIComponent(this.ssTarget());
      downloadFile(url, "riskfinder.pptx", onDlComplete);
    }
  };

  handleScenarioChange = idx => {
    this.props.dispatch(SetEAEScenario(this.props.presets[idx]));
    //this.setState({scenario: this.props.presets[idx]})
  };

  handleDerivativeChange = value => {
    this.props.dispatch(SetEAEDerivative(value));
    //this.setState({derivative: value !== "exposure"});
  };

  handleSensitivityChange = value => {
    this.props.dispatch(SetEAESensitivity(value));
    //this.setState({percentile: value})
  };

  handlePercentageChange = isPct => {
    this.props.dispatch(SetEAEIsPct(isPct));
    this.setState({percentage: isPct});
  };

  handleVariableChange = value => {
    this.props.dispatch(SetEAEVariable(value));
    //this.setState({variable: value});
  };

  filterData(data, predicate) {
    if (predicate) {
      return data.find(d => predicate(d));
    } else {
      return data.find(d => {
        return (d.scenario === this.props.eaeScenario &&
        d.percentile === this.props.eaePercentile &&
        d.variable === this.props.eaeVariable)
      });
    }
  }

  getEAE(d) {
    return t => d.logistic_param_t0 + (d.logistic_param_t1 - d.logistic_param_t0) /
                                       (1 + Math.exp(-d.logistic_param_k * (t - d.logistic_param_m)));
  }

  getEAEdydx(d) {
    return t => d.logistic_param_k * (d.logistic_param_t1 - d.logistic_param_t0) * (
            Math.exp(-d.logistic_param_k * (t - d.logistic_param_m)) / Math.pow(Math.exp(-d.logistic_param_k * (t - d.logistic_param_m)) + 1, 2))
  }

  changeVariable = () => {
    const vars = ["Population", "Housing", "Property", "Roads"];
    this.varToggleCount += 1;
    this.handleVariableChange(vars[this.varToggleCount % vars.length]);
    //this.setState({variable: vars[this.varToggleCount % vars.length]});
  };

  togglePercentage = () => {
    this.handlePercentageChange(!this.props.eaeIsPct);
  };

  varName(variable) {
    if (variable === "Population") {
      return "population";
    } else if (variable === "Housing") {
      return "housing units";
    } else if (variable === "Roads") {
      return "road miles";
    } else if (variable === "Property") {
      return "property value";
    }
  }

  render() {
    const { placeName, data, totals }  = this.props;

    if (data.length === 0 || totals.length === 0) {
      return (<div className="RegionEAE main">
        <a className="anchor" id="expected-annual-exposure"/>
      </div>);
    }

    const filtered = this.filterData(data);
    const eaeF = this.getEAE(filtered);
    const eaeFprime = this.getEAEdydx(filtered);
    const xRange = d3.range(2000, 2101, 10);
    var graphData = xRange.map(year => {return {
      x: year,
      y: this.props.eaeDerivative ? eaeFprime(year) * 10 : eaeF(year),
      text: `${this.props.eaeDerivative ? Math.round(10000 * eaeFprime(year) * 10) / 100 : (Math.round(1000 * eaeF(year)) / 10)}%`
    }});
    const dataMax = this.filterData(data, d => {
      return d.variable === this.props.eaeVariable && d.scenario === "RCP 8.5" && d.percentile === 95
    });
    const eaeFMax = this.getEAE(dataMax);
    const eaeFprimeMax = this.getEAEdydx(dataMax);

    var graphMax = d3.max(xRange.map(year =>  this.props.eaeDerivative ? eaeFprimeMax(year) : eaeFMax(year)));
    if (!this.props.eaeIsPct) {
      graphData = graphData.map(pt => { return {
        x: pt.x,
        y: pt.y * totals[this.props.eaeVariable],
        text: this.props.eaeDerivative ? commas(Math.round(10 * pt.y * totals[this.props.eaeVariable]) / 10)
            : commas(Math.round(pt.y * totals[this.props.eaeVariable]))
      }});
      graphMax *= totals[this.props.eaeVariable];
    }
    const chartMargin = {top: 20, right: 20, bottom: 60, left: 105};
    if (!(this.props.eaeDerivative && this.props.eaeIsPct) && (this.props.eaeIsPct || graphMax < 1000)) {
      chartMargin.left = 90;
    }
    if (graphMax > 100000) {
      chartMargin.left = 120;
    }

    const isChecked = name => this.props.eaeScenario === name;

    var scenarioText;
    if (this.props.eaeScenario === "RCP 2.6") {
      scenarioText = "assuming extreme cuts in heat-trapping pollution (RCP 2.6)";
    } else if (this.props.eaeScenario === "RCP 4.5") {
      scenarioText = "assuming moderate cuts in heat-trapping pollution (RCP 4.5)";
    } else if (this.props.eaeScenario === "RCP 8.5") {
      scenarioText = "assuming unchecked pollution (RCP 8.5)";
    } else {
      scenarioText = "";
    }

    var percentileText;
    if (this.props.eaePercentile === 50) {
      percentileText = "median";
    } else {
      percentileText = `${this.props.eaePercentile}th percentile`;
    }
    percentileText = xlt(percentileText);

    var yLabel;
    if (!this.props.eaeIsPct && this.props.eaeVariable === "Property") {
      graphData = graphData.map(pt => {return {
        x: pt.x,
        y: pt.y / 1000000,
        text: `$${commas(Math.round(pt.y / 1000000))} ` + xlt("million")
      }});
      graphMax /= 1000000;
      yLabel = `${capitalize(this.varName(this.props.eaeVariable))} ($M)${this.props.eaeDerivative ? xlt(": growth rate per decade") : ""}`;
    } else {
      yLabel = `${this.props.eaeIsPct ?
          xlt(`% of ${this.varName(this.props.eaeVariable)}`) :
          capitalize(this.varName(this.props.eaeVariable))}${this.props.eaeDerivative ? xlt(": growth rate per decade") : ""}`;
    }

    return (
      <div className="RegionEAE main">
        <div className="header">
          <a className="anchor" id="expected-annual-exposure"/>
          <Link className="hash-link" to={sameLocationNewHash("#expected-annual-exposure")}>
            <h1>Projected Growth in Flood Exposure <span className="anchor-hash"><emph>#</emph></span></h1>
          </Link>
          <ComponentIcons hasDownload={false} openSettings={this.openSettings.bind(this)} openInfo={this.openInfo} />
        </div>
        <div className="tab-group">
          <h3 className={"impactTab " + (this.props.eaeDerivative ? "" : "active")} onClick={this.handleDerivativeChange.bind(this, false)}>Exposure</h3>
          <h3 className={"impactTab " + (this.props.eaeDerivative ? "active" : "")} onClick={this.handleDerivativeChange.bind(this, true)}>Exposure growth rates</h3>
        </div>
        <h2>
          <span className="eaeIsPct">{this.props.eaeDerivative ?
            <span>
              <span>Growth in expected</span>
              <span>&nbsp;</span>
              <ToggleText>
                <span onClick={this.togglePercentage}>
                  <var>{this.props.eaeIsPct ? xlt("percentage of") : xlt("total")}</var>
                </span>
              </ToggleText>
            </span>
               :
            <ToggleText>
              <span onClick={this.togglePercentage}>
                <var>{this.props.eaeIsPct ? xlt('Percentage of') : xlt('Total')}</var>
              </span>
            </ToggleText>
            }
          </span>
          <span>&nbsp;</span>
          <span className="eaeVariable">
            <ToggleText>
              <span onClick={this.changeVariable.bind(this)}>
                <var>{xlt(this.props.eaeVariable.toLowerCase())}</var>
              </span>
            </ToggleText>
          </span>
          <span> in </span>
          <var>{xlt(simplePlaceName(placeName))} </var>
          <span>exposed to flooding</span>
          <svg className="download-icon png" version="1.1" viewBox="0 0 585.918 585.918" onClick={this.handleDownloadClick.bind(null, "eae-img")}>
            {pngIcon}
          </svg>
        </h2>
          <BarChart
            id="region-eae-chart"
            data={graphData}
            yMax={graphMax}
            isWaterLevel={!this.props.eaeIsPct}
            margin={chartMargin}
            width={this.width}
            height={this.height}
            xLabel={xlt("Year")}
            yLabel={yLabel}
        >
          <PresetButtons
              forecastPresets={this.props.presets}
              scenario={this.props.eaeScenario}
              handleAdvancedClick={this.openSettings.bind(this, "#select-eae-scenario")}
              groupName={"Climate pollution scenario"}
              callback={this.handleScenarioChange}
          />
        </BarChart>
        <div className="caption">
          <p ref={this.setTxContent}>Main source: <a href="http://dx.doi.org/10.1007/s10584-017-1963-7" target="_blank">Kulp and Strauss 2017</a>, Climatic Change. Sea level projections: {percentileText} local projections {scenarioText}. (<a href="http://onlinelibrary.wiley.com/doi/10.1002/2014EF000239/abstract" target="_blank">Kopp et al. 2014</a>, Earth's Future).</p>
          <p>See: <a href="http://dx.doi.org/10.1007/s10584-017-1963-7" target="_blank">original paper</a> | <a href="http://reports.climatecentral.org/atlantic-city/sea-level-rise/#iframe-container" target="_blank">national interactive</a> | <a href="http://reports.climatecentral.org/atlantic-city/sea-level-rise/" target="_blank">related coverage</a></p>
        </div>
        <Modal isOpen={this.state.settingsOpen && this.props.modalVisible} handleClick={this.closeSettings} title="Growth in Exposure Settings">
          <h3>Chart type</h3>
          <p>Function</p>
          <label>
            <input type="radio" onChange={this.handleDerivativeChange.bind(this, false)} checked={!this.props.eaeDerivative} />
            Expected exposure</label>
          <label>
            <input type="radio" onChange={this.handleDerivativeChange.bind(this, true)} checked={this.props.eaeDerivative} />
            Expected exposure rate of change</label>
          <p>Percent/total</p>
          <label>
            <input type="radio" onChange={this.handlePercentageChange.bind(this, true)} checked={this.props.eaeIsPct} />
            Percentage</label>
          <label>
            <input type="radio" onChange={this.handlePercentageChange.bind(this, false)} checked={!this.props.eaeIsPct} />
            Total</label>
          <h3 id="select-eae-scenario">Carbon emissions scenario</h3>
          <p>The RCP scenarios are standard scenarios widely used by scientists and the Intergovernmental Panel on Climate Change.</p>
          <label>
            <input type="radio" value="High" onChange={this.handleScenarioChange.bind(this, 2)} checked={isChecked("RCP 8.5")} />
            Unchecked pollution (RCP 8.5)</label>
          <label>
            <input type="radio" value="Medium" onChange={this.handleScenarioChange.bind(this, 1)} checked={isChecked("RCP 4.5")} />
            Moderate carbon cuts (RCP 4.5)</label>
          <label>
            <input type="radio" value="Low" onChange={this.handleScenarioChange.bind(this, 0)} checked={isChecked("RCP 2.6")} />
            Extreme carbon cuts (RCP 2.6)</label>
          <h3 id="select-eae-sensitivity">Model sensitivity</h3>
          <label>
            <input type="radio" onChange={this.handleSensitivityChange.bind(this, 5)} checked={this.props.eaePercentile === 5} />
            5th percentile</label>
          <label>
            <input type="radio" onChange={this.handleSensitivityChange.bind(this, 50)} checked={this.props.eaePercentile === 50} />
            50th percentile (median)</label>
          <label>
            <input type="radio"  onChange={this.handleSensitivityChange.bind(this, 95)} checked={this.props.eaePercentile === 95} />
            95th percentile</label>
          <h3 id="select-eae-variable">Exposure category</h3>
          <label>
            <input type="radio" onChange={this.handleVariableChange.bind(this, "Housing")} checked={this.props.eaeVariable === "Housing"} />
            Housing</label>
          <label>
            <input type="radio" onChange={this.handleVariableChange.bind(this, "Population")} checked={this.props.eaeVariable === "Population"} />
            Population</label>
          <label>
            <input type="radio"  onChange={this.handleVariableChange.bind(this, "Property")} checked={this.props.eaeVariable === "Property value"} />
            Property value</label>
          <label>
            <input type="radio"  onChange={this.handleVariableChange.bind(this, "Roads")} checked={this.props.eaeVariable === "Roads"} />
            Miles of road</label>
          <br />
        </Modal>
        <Modal
            isOpen={this.state.downloadOpen && this.props.modalVisible}
            handleClick={this.closeDownload}
        >
          <DownloadForm
              id="ForecastDLForm"
              dlType={this.state.dlType}
              productID="eae"
              submitted={!this.state.downloadOpen}
              dlCallback={this.dlCallback}
              containerClass="download"
          >
          </DownloadForm>
        </Modal>
      </div>
    );
  };
}

export default withStyles(styles)(RegionEAE);
